import { useMemo } from 'react';
import type React from 'react';

import { Field, useField } from 'react-final-form';

import { Select } from '@change/ds-forms';

import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useFormI18n } from '#src/app/shared/form/hooks';
import { requiredValidator } from '#src/app/shared/form/validators';

import { useCategoryLabel } from '../hooks/useCategoryLabel';
import { getSubCategories } from '../shared/subCategories';
import type { FormValues, ReportType, SubCategory } from '../shared/types';

type Props = Readonly<{
	type: ReportType;
}>;

const validator = requiredValidator<SubCategory>({
	i18n: { key: translationKey('fe.components.petition_report_abuse.errors.sub_category_required') },
});

export const SubCategoryField: React.FC<Props> = ({ type }) => {
	const {
		input: { value: category },
	} = useField<FormValues['category']>('category', { subscription: { value: true } });

	const subCategories = useMemo(() => (category ? getSubCategories(type, category) : []), [type, category]);

	const { translate } = useI18n();
	const { getErrorMessage } = useFormI18n();

	const getLabel = useCategoryLabel(type, 'subcategory');

	const subCategoryItems = useMemo(
		() => [
			{ id: '', name: '', label: translate('fe.components.petition_report_abuse.sub_category_prompt') },
			...subCategories.map((subCategory) => ({ id: subCategory, name: subCategory, label: getLabel(subCategory) })),
		],
		[translate, subCategories, getLabel],
	);

	if (!subCategories.length) return null;

	return (
		<div className="mb-2">
			<Field<FormValues['subCategory']> name="subCategory" validate={validator}>
				{({ input, meta }) => (
					<Select
						id="report-abuse-subcategory"
						name={input.name}
						selectedKey={input.value}
						onSelectionChange={input.onChange}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
						errorMessage={meta.touched && getErrorMessage(meta.error)}
						isInvalid={meta.touched && !!meta.error}
						autoComplete="country"
						data-testid="reportAbuse-subCategory-select"
						aria-label={translate('fe.components.petition_report_abuse.sub_category_aria_label')}
						label={translate('fe.components.petition_report_abuse.sub_category_aria_label')}
						// keep the label aria-only — the legacy field showed no visible label
						labelIsVisible={false}
						items={subCategoryItems}
					/>
				)}
			</Field>
		</div>
	);
};
